/* salesLadderSetup.tsx — Sofi's guided setup for the Sales Ladder page.

   This is the ONLY page-specific file for the tutorial: an imperative bridge the
   page implements + the ordered step script. The engine (../../Tutorial) is
   page-agnostic. When this setup finishes, the account has a real, content-backed
   ladder and the sales-ladder automation regime switched on — i.e. it's live.

   v1 covers the recommended Fixed path end-to-end. Adaptable is a lighter guided
   branch (see steps gated on `!isFixed`).

   All copy is i18n'd under the `salesLadder` namespace (`setup.*`). The step
   script is built per-render from the caller's `t`, and the two rich-markup
   bodies are components that pull their own translations. */

import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import type { Section } from './sections';
import type { TutorialDef, TutorialStep } from '../../Tutorial';
import { Option, Icon, C } from '../../Onboarding/onboardingKit';

/** Stable id — used for the "seen" check independently of the built def. */
export const SALES_LADDER_SETUP_ID = 'sales-ladder-setup';

/** Imperative handle the SalesLadder page hands the tutorial engine. Built fresh
 * each render so every method reads current state (the engine snapshots it on a
 * poll for auto-advance). */
export interface LadderBridge {
  isFixed: () => boolean;
  /** Select + persist Fixed mode (is_fixed_ladder_active = true). */
  chooseFixed: () => void | Promise<void>;
  gotoSection: (s: Section) => void;
  /** Open the ladder editor in create mode (seeded with the starter rungs). */
  openNewLadder: () => void;
  /** Show the ladders list (close any open editor) so the list can be spotlit. */
  showLadderList: () => void;
  isEditorOpen: () => boolean;
  /** Whether the open editor passes validation — every rung has media, prices valid. */
  isEditorReady: () => boolean;
  laddersCount: () => number;
  /** Flip on the sales-ladder automation regime for the account. */
  activateRegime: () => void | Promise<void>;
}

/** Fixed vs Adaptable as a scannable side-by-side comparison, with Fixed flagged
 * as the recommended starting point (the slide's button commits to Fixed).
 * Built from the passed translator so it stays a plain body node, not a
 * standalone component (keeps this file free of fast-refresh mixed exports). */
function modeCompareBody(t: TFunction): ReactNode {
  return (
    <div>
      <div style={{ marginBottom: 14 }}>{t('setup.mode.lead')}</div>
      <div className="mobile-stack-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Option
          layout="card"
          selected
          badge={t('setup.mode.recommended')}
          icon={<Icon name="ladder" size={20} />}
          title={t('setup.mode.fixedTitle')}
          desc={t('setup.mode.fixedDesc')}
        />
        <Option
          layout="card"
          icon={<Icon name="convert" size={20} />}
          title={t('setup.mode.adaptableTitle')}
          desc={t('setup.mode.adaptableDesc')}
        />
      </div>
      <div
        style={{
          marginTop: 14, display: 'flex', alignItems: 'center', gap: 9, fontSize: 13.5, lineHeight: 1.5,
          color: C.purpleHi, background: 'color-mix(in srgb, var(--om-accent) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--om-accent) 22%, transparent)',
          borderRadius: 12, padding: '10px 13px',
        }}
      >
        <Icon name="check" size={16} />
        <span>
          <Trans t={t} i18nKey="setup.mode.callout" components={[<strong style={{ color: C.text }} />]} />
        </span>
      </div>
    </div>
  );
}

/** "How your ladders are used" — a bulleted list with bold fragments. */
function laddersUsedBody(t: TFunction): ReactNode {
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Trans t={t} i18nKey="setup.laddersUsed.lead" components={[<strong />]} />
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <li><Trans t={t} i18nKey="setup.laddersUsed.item1" components={[<strong />]} /></li>
        <li><Trans t={t} i18nKey="setup.laddersUsed.item2" components={[<strong />]} /></li>
        <li>{t('setup.laddersUsed.item3')}</li>
      </ul>
      <div style={{ marginTop: 8 }}>{t('setup.laddersUsed.footer')}</div>
    </div>
  );
}

/** Build the ordered step script from the caller's translator. */
function buildSteps(t: TFunction): TutorialStep<LadderBridge>[] {
  return [
    {
      id: 'intro',
      phase: 'concept',
      pose: 'wave',
      title: t('setup.steps.introTitle'),
      body: t('setup.steps.introBody'),
      primaryLabel: t('setup.steps.introPrimary'),
    },
    {
      id: 'mode-choice',
      phase: 'concept',
      pose: 'explain',
      title: t('setup.steps.modeTitle'),
      body: modeCompareBody(t),
      primaryLabel: t('setup.steps.modePrimary'),
      action: { hint: '', do: (c) => c.chooseFixed() },
    },
    {
      id: 'mode-anchor',
      phase: 'coach',
      pose: 'point',
      anchor: 'sl-mode',
      placement: 'bottom',
      title: t('setup.steps.modeAnchorTitle'),
      body: t('setup.steps.modeAnchorBody'),
      onEnter: (c) => c.gotoSection('ladders'),
      action: { hint: t('setup.steps.modeAnchorHint') },
    },
    {
      id: 'seed',
      phase: 'coach',
      pose: 'explain',
      anchor: 'sl-rungs',
      placement: 'top',
      title: t('setup.steps.seedTitle'),
      body: t('setup.steps.seedBody'),
      onEnter: (c) => c.openNewLadder(),
      action: { hint: t('setup.steps.seedHint') },
    },
    {
      id: 'attach',
      phase: 'coach',
      pose: 'point',
      anchor: 'sl-addmedia',
      placement: 'left',
      title: t('setup.steps.attachTitle'),
      body: t('setup.steps.attachBody'),
      // Hard gate: you literally can't save a ladder without media on every rung.
      gate: true,
      primaryLabel: t('setup.steps.attachPrimary'),
      action: { hint: t('setup.steps.attachHint'), advanceWhen: (c) => c.isEditorReady() },
    },
    {
      id: 'more-sales',
      phase: 'coach',
      pose: 'explain',
      anchor: 'sl-addsale',
      placement: 'top',
      title: t('setup.steps.moreSalesTitle'),
      body: t('setup.steps.moreSalesBody'),
      optional: true,
    },
    {
      id: 'save',
      phase: 'coach',
      pose: 'point',
      anchor: 'sl-save',
      placement: 'top',
      title: t('setup.steps.saveTitle'),
      body: t('setup.steps.saveBody'),
      // Advances only on a real save (editor closes + ladder count rises), so the
      // button can't lie about progress. Disabled until then.
      gate: true,
      primaryLabel: t('setup.steps.savePrimary'),
      action: { hint: t('setup.steps.saveHint'), advanceWhen: (c) => !c.isEditorOpen() && c.laddersCount() >= 1 },
    },
    {
      id: 'add-more-ladders',
      phase: 'coach',
      pose: 'point',
      anchor: 'sl-add-ladder',
      placement: 'left',
      title: t('setup.steps.addMoreTitle'),
      body: t('setup.steps.addMoreBody'),
      onEnter: (c) => c.showLadderList(),
      optional: true,
    },
    {
      id: 'ladders-used',
      phase: 'coach',
      pose: 'explain',
      anchor: 'sl-ladder-list',
      placement: 'top',
      title: t('setup.steps.laddersUsedTitle'),
      body: laddersUsedBody(t),
      onEnter: (c) => c.showLadderList(),
      optional: true,
    },
    {
      id: 'negotiation',
      phase: 'coach',
      pose: 'point',
      anchor: 'sl-negotiation',
      placement: 'top',
      title: t('setup.steps.negotiationTitle'),
      body: t('setup.steps.negotiationBody'),
      onEnter: (c) => c.gotoSection('negotiation'),
      optional: true,
    },
    {
      id: 'alerts',
      phase: 'coach',
      pose: 'okay',
      anchor: 'sl-alerts',
      placement: 'top',
      title: t('setup.steps.alertsTitle'),
      body: t('setup.steps.alertsBody'),
      onEnter: (c) => c.gotoSection('alerts'),
      optional: true,
    },
    {
      id: 'prompts',
      phase: 'coach',
      pose: 'think',
      anchor: 'sl-prompts',
      placement: 'top',
      title: t('setup.steps.promptsTitle'),
      body: t('setup.steps.promptsBody'),
      onEnter: (c) => c.gotoSection('prompts'),
      optional: true,
    },
    {
      id: 'go-live',
      phase: 'concept',
      pose: 'okay',
      title: t('setup.steps.goLiveTitle'),
      body: t('setup.steps.goLiveBody'),
      primaryLabel: t('setup.steps.goLivePrimary'),
      action: { hint: '', do: (c) => c.activateRegime() },
    },
    {
      id: 'done',
      phase: 'concept',
      pose: 'wave',
      title: t('setup.steps.doneTitle'),
      body: t('setup.steps.doneBody'),
      primaryLabel: t('setup.steps.donePrimary'),
    },
  ];
}

/** Build the tutorial definition with copy bound to the caller's translator. */
export function buildSalesLadderSetupDef(t: TFunction): TutorialDef<LadderBridge> {
  return {
    id: SALES_LADDER_SETUP_ID,
    steps: buildSteps(t),
  };
}
